import React, { useEffect, useState, useCallback } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';

import Table from '@app/components/table';
import { TableTitle } from '@app/components/table/table.type';
import Icon from '@app/components/ui/icons';
import { PaginationResponse } from '@app/components/ui/paginator/paginator';
import {
  MFStatisticRequestType,
  StatisticWidgetsRangeType,
} from '@app/interfaces/qm-module/qm-statistic/qm-statistic.type';
import TimeDuration from '@app/pages/analytics/project-records/blocks/time-duration';
import {
  useGetStatisticMFSListQuery,
  useLazyGetStatisticMFSListExportQuery,
} from '@app/store/api/qm-module/qm-statistic/qm-statistic-page.api';
import { Button, CircleButton, DropMenu, FormTabSwitcher, Paginator, Toggle } from '@ui';

import { navigationRoutes } from '@app/utils/navigation-routes';
import { useGetUserInfoQuery } from '@app/store/api/get-user-info.api';
import useTranslation from '@app/hooks/use-translation';
import { StatisticColorByStepCalc } from '@app/utils/statistic-color-by-step';
import { DropMenuItem } from '@app/components/ui/drop-menu/drop-menu.type';
import { AppealFilter } from '@app/pages/qm-module/qm-appeal/blocks';
import { AppealFilterType } from '@app/pages/qm-module/qm-appeal/blocks/appeal-filter/types';
import { OptionItem } from '@app/components/ui/select/select.type';

import TableSkeleton from '@app/components/table/table-skeleton';

import ChartDatePickerModalBlock from '@app/pages/reports/blocks/chart-date-picker-modal.block';

import fileDownload from 'js-file-download';

import { useLazyGetRecordMonitoringDataExportQuery } from '@app/store/api/qm-module/qm-manage/qm-manage-monitoring-tasks.api';

import { useGetAliasesListQuery } from '@app/store/api/alias-settings-page.api';

import { StatisticOutletContextType } from '../../type';
import RangeBlock from '../range-block';
import { useGetMonitoringFilterData } from '../use-get-monitoring-filter-data';
import { useGetStatisticLinkObject } from '../../hooks/useGetStatisticLinkObject';

const MonitoringTapContent: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data } = useGetAliasesListQuery();

  const { t } = useTranslation('pages.qmManage.statistic');
  const { controllersOptions, getOperatorsByTeams, setNavigationItems } =
    useOutletContext<StatisticOutletContextType>();

  const [isShowModelDatePicker, changeShowingModelDatePicker] = useState<boolean>(false);

  const { updateUrl, widgetsFilterBlock } = useGetStatisticLinkObject(setNavigationItems);

  const [mfsRequestData, setRequestData] = useState<Omit<MFStatisticRequestType, 'params'>>({
    body: {
      offset: 0,
      limit: 10,
      range: widgetsFilterBlock?.range || {},
      filter: widgetsFilterBlock?.operators?.length
        ? [{ field: 'operator', value: widgetsFilterBlock.operators, condition: '=' }]
        : [],
    },
  });
  // api

  useEffect(() => {
    setRequestData((prev) => ({
      ...prev,
      body: {
        ...prev.body,
        range: widgetsFilterBlock?.range || {},
        filter: widgetsFilterBlock?.filter || [],
        offset: 0,
        limit: 10,
        da: widgetsFilterBlock?.da,
      },
    }));
  }, [widgetsFilterBlock]);

  const [operatorsOptions, setOperatorsOptions] = useState<OptionItem[]>([]);
  useEffect(() => {
    if (!getOperatorsByTeams) return;

    async function getOperators() {
      try {
        const res = await getOperatorsByTeams(widgetsFilterBlock?.teams || []);
        setOperatorsOptions(res);
      } catch {
        setOperatorsOptions([]);
      }
    }
    getOperators();
  }, [getOperatorsByTeams, widgetsFilterBlock]);

  const { monitoringFilterKeys, additionalOptions } = useGetMonitoringFilterData({
    operators: operatorsOptions,
    controllers: controllersOptions,
    aliases: data?.aliases,
  });

  const [exportMonitoringAllData] = useLazyGetStatisticMFSListExportQuery();
  const [exportRecordMonitoringData] = useLazyGetRecordMonitoringDataExportQuery();

  const onExportMonitoringAllData = useCallback(
    async function (format: 'csv' | 'xls') {
      if (!id) return;
      const body = {
        ...mfsRequestData.body,
        limit: undefined,
        offset: undefined,
        format: format.toUpperCase(),
      };
      const file = await exportMonitoringAllData({ body, params: { mform_id: id } });
      file?.data && fileDownload(file?.data, `${id}.${format === 'csv' ? format : format + 'x'}`);
    },
    [exportMonitoringAllData, id, mfsRequestData.body],
  );
  const onExportMonitoring = useCallback(
    async function (name: string, id: string) {
      if (!id) return;
      const file = await exportRecordMonitoringData({ id });
      file?.data && fileDownload(file.data, `${name}.xlsx`);
    },
    [exportRecordMonitoringData],
  );

  const { data: mfsList, isLoading } = useGetStatisticMFSListQuery(
    { ...mfsRequestData, params: { mform_id: id || '' } },
    { skip: !id, refetchOnMountOrArgChange: true },
  );

  const { data: userInfo } = useGetUserInfoQuery();

  function handlerChangeCustomRangeInterval({ start, end }) {
    setRequestData((prev) => {
      const changedObj = {
        ...prev,
        body: {
          ...prev.body,
          range: {
            dt_type: prev.body.range?.dt_type,
            type: 'r' as const,
            parameters: [
              { condition: '>' as const, value: start as string },
              { condition: '<' as const, value: end as string },
            ],
          },
        },
      };
      updateUrl(changedObj.body);
      return changedObj;
    });
  }

  const handlerDateDropTrigger = useCallback(
    (key: StatisticWidgetsRangeType['type']) => {
      if (key === 'r') {
        changeShowingModelDatePicker(true);
        return;
      }
      setRequestData((prev) => {
        const changedObj = {
          ...prev,
          body: {
            ...prev.body,
            range: {
              dt_type: prev.body.range?.dt_type,
              type: key,
              parameters: [] as [],
            },
          },
        };
        updateUrl(changedObj.body);
        return changedObj;
      });
    },
    [updateUrl],
  );

  const onChangeFilter = useCallback(
    (filter: AppealFilterType) => {
      setRequestData((prev) => {
        const changedObj = { ...prev, body: { ...prev.body, ...filter } };
        updateUrl(changedObj.body);
        return changedObj;
      });
    },
    [updateUrl],
  );

  const handleChangeAutoMonitoringToggle = useCallback(
    (value: boolean) => {
      setRequestData((prev) => {
        const changedObj = { ...prev, body: { ...prev.body, da: value } };

        updateUrl(changedObj.body);
        return changedObj;
      });
    },
    [updateUrl],
  );
  t('monitoring_tap_table');
  const tableColumns: TableTitle[] = [
    { index: 'duration', title: t('monitoring_tap_table.duration') },
    { index: 'ucid', size: 250, title: t('monitoring_tap_table.call_id') },
    { index: 'dt_start', title: t('monitoring_tap_table.start_date') },
    { index: 'dt_completion', title: t('monitoring_tap_table.check_date') },
    { index: 'controller', title: t('monitoring_tap_table.controller') },
    { index: 'score', title: t('monitoring_tap_table.final_score') },
    { index: 'operator', title: t('monitoring_tap_table.operator') },
    { index: 'team', title: t('monitoring_tap_table.team') },
    { index: 'appellation', title: t('monitoring_tap_table.appeal') },
    { index: 'confirmed', title: t('monitoring_tap_table.confirmed') },
    { index: 'actions', truncate: false, title: t('monitoring_tap_table.actions') },
  ];

  const downloadDropMenu: DropMenuItem[] = [
    { title: t('download_as_csv'), key: 'csv' },
    { title: t('download_as_xls'), key: 'xls' },
  ];

  const tableData = React.useMemo(() => {
    if (!mfsList?.mfs) return [];

    return mfsList.mfs.map((record, index) => ({
      ...record,
      duration: (
        <div className="flex cursor-pointer">
          <TimeDuration
            link={`/${navigationRoutes.qm_manage_monitoring_tasks}/${record.mfs_id}/${record.record_id}`}
            duration={record.duration}
          />
        </div>
      ),
      score: (
        <div className="w-full float-left">
          <div
            style={{
              width: `${record.score * 1.5 + 40}px`,
              backgroundColor: StatisticColorByStepCalc({
                max: mfsList.mform?.score_max_calc || 100,
                score: record.score,
              }),
            }}
            className="h-[23px] text-[15px] text-white font-[400] leading-[18px] flex items-center p-[3px_9px_2px_0px] justify-end rounded-[2px]"
          >
            {record.score}
          </div>
        </div>
      ),
      actions: (
        <div
          style={{ zIndex: mfsList.mfs.length - index + 1 }}
          className="absolute flex items-center gap-[5px]"
        >
          <CircleButton
            hintTitle={t('hints.monitoring_view')}
            icon="Oscilogram"
            size={22}
            onClick={() =>
              window.open(
                `/${navigationRoutes.qm_manage_monitoring_tasks}/${record.mfs_id}/${record.record_id}`,
                '_blank',
              )
            }
          />
          <CircleButton
            hintTitle={t('hints.monitoring_download')}
            onClick={() => onExportMonitoring(record.ucid + '.xlsx', record.mfs_id)}
            icon="DownloadCloudIcon"
            size={18}
            className="text-3color"
          />
        </div>
      ),
      appellation: (
        <div>
          <Icon
            className={record.appellation ? 'text-basic_green' : 'text-basic_red'}
            name="CircleIcon"
          />
        </div>
      ),
      confirmed: (
        <div>
          <Icon
            className={record.confirmed ? 'text-basic_green' : 'text-basic_red'}
            name="CircleIcon"
          />
        </div>
      ),
    }));
  }, [mfsList?.mform?.score_max_calc, mfsList?.mfs, onExportMonitoring, t]);

  const filter = mfsRequestData.body.filter;
  const ufilter = mfsRequestData.body.ufilter;

  const dtTypeValuesList = [
    { value: 'start', title: t('dt_type.start') },
    { value: 'processing', title: t('dt_type.processing') },
  ];

  function handleChangeDtType(value: string) {
    setRequestData((prev) => {
      const changedObj: { body: MFStatisticRequestType['body'] } = {
        ...prev,
        body: { ...prev.body, range: { ...prev.body.range, dt_type: value as 'start' } },
      };

      updateUrl(changedObj.body);
      return changedObj;
    });
  }

  return (
    <>
      <div>
        <AppealFilter
          data={{ filter, ufilter }}
          keysForFiltering={monitoringFilterKeys}
          onChangeHandler={onChangeFilter}
          additionalFilterKeys={[
            {
              keyName: 'ufilter',
              options: additionalOptions,
              buttonLabel: t('filter.metaFilterButtonLabel'),
              fieldName: 'id',
            },
          ]}
        />
      </div>

      <div className="flex w-full items-center  pt-[10px] gap-[20px]">
        <div className="flex items-center gap-[5px] w-fit ">
          <Toggle checked={mfsRequestData.body.da} onChecked={handleChangeAutoMonitoringToggle} />
          <span className="whitespace-nowrap">{t('show_auto_monitoring')}</span>
        </div>
        <div className="flex items-center justify-end w-full gap-[40px]">
          <div className="w-full ">
            <FormTabSwitcher
              classname="!bg-[#efefef]"
              valuesList={dtTypeValuesList}
              isActive={mfsRequestData.body.range?.dt_type || 'start'}
              onChange={handleChangeDtType}
            />
          </div>
          <div className="min-w-[320px] flex justify-end">
            <RangeBlock
              rangeFromStor={mfsRequestData.body.range}
              activeKey={mfsRequestData.body.range?.type}
              keysToHide={['n']}
              onChange={(key) => handlerDateDropTrigger(key as StatisticWidgetsRangeType['type'])}
            />
          </div>
          <DropMenu
            dropSize={200}
            onClick={(key) => onExportMonitoringAllData(key as 'csv' | 'xls')}
            menu={downloadDropMenu}
          >
            <Button label={t('download')} fill="linked" />
          </DropMenu>
        </div>
      </div>
      <div className="py-[20px] flex flex-col gap-[20px]">
        {isLoading ? (
          <TableSkeleton />
        ) : (
          <Table
            isPending={isLoading}
            default_width={7}
            hideDragMode
            columns={
              userInfo?.qm_role === 'operator'
                ? tableColumns.filter((col) => col.index !== 'controller')
                : tableColumns
            }
            dataSource={tableData || []}
          />
        )}

        <Paginator
          key={mfsRequestData?.body?.offset}
          page={(mfsRequestData?.body?.offset || 0) + 1}
          limit={mfsRequestData?.body?.limit}
          count={mfsList?.total || 0}
          onChange={function ({ offset, limit }: PaginationResponse) {
            setRequestData((prev) => ({ ...prev, body: { ...prev.body, limit, offset } }));
          }}
        />
      </div>
      <ChartDatePickerModalBlock
        dataIdStartDate="statistic-interval-date-from"
        dataIdEndDate="statistic-interval-date-to"
        onSuccess={handlerChangeCustomRangeInterval}
        show={isShowModelDatePicker}
        onClose={() => changeShowingModelDatePicker(false)}
        defaultDates={
          mfsRequestData.body.range?.type === 'r'
            ? {
                start: mfsRequestData.body.range.parameters?.find((p) => p.condition === '>')
                  ?.value,
                end: mfsRequestData.body.range.parameters?.find((p) => p.condition === '<')?.value,
              }
            : undefined
        }
      />
    </>
  );
};

export default MonitoringTapContent;
